import { Box, Flex, usePrefersReducedMotion } from '@chakra-ui/react';
import { t } from '@lingui/core/macro';
import { pxToRem } from '@duke-hq/ui/constants/scalable';
import { BAR_SEGMENT_TRANSITION } from './translationsMotion';

// The counts a stacked coverage bar draws, in reading order. Shared by the
// coverage page's per-language rows and the course toolbar pill's bars so the
// two can never render the same state differently.
export type CoverageBarCounts = {
  // Fully translated, no English gaps.
  ready: number;
  // Translated but partly in English: rendered as hatched green, "done with an
  // asterisk", never a new alarm colour.
  gaps: number;
  inProgress: number;
  attention: number;
  notStarted: number;
};

const GAP_HATCH =
  'repeating-linear-gradient(135deg, var(--chakra-colors-green-500) 0px, var(--chakra-colors-green-500) 3px, var(--chakra-colors-green-200) 3px, var(--chakra-colors-green-200) 6px)';

// Segments + a worded count string for the tooltip. Ordered done, gaps, attention,
// in-flight, untranslated, so the bar reads left-to-right as progress. Zero-value
// segments are kept (flex-grow 0, invisible) so a live bar glides a segment in or
// out instead of popping it; the tooltip summary names only the non-zero ones.
export function coverageBarSegments(counts: CoverageBarCounts) {
  const segments = [
    { key: 'ready', value: counts.ready, bg: 'green.500', label: t`translated` },
    { key: 'gaps', value: counts.gaps, bg: GAP_HATCH, label: t`partly in English` },
    { key: 'attention', value: counts.attention, bg: 'red.500', label: t`need review` },
    { key: 'inProgress', value: counts.inProgress, bg: 'blue.500', label: t`translating` },
    {
      key: 'notStarted',
      value: counts.notStarted,
      bg: 'border.emphasis',
      label: t`not translated`,
    },
  ];
  const present = segments.filter((segment) => segment.value > 0);
  return {
    segments,
    total: counts.ready + counts.gaps + counts.inProgress + counts.attention + counts.notStarted,
    summary: present.map((segment) => `${segment.value} ${segment.label}`).join(' · '),
  };
}

// Thin stacked progress bar. Blue appears only for genuinely in-flight work; the
// untranslated remainder is a neutral track, so a resting partial set reads
// mostly-grey, never a full bar. Callers own the tooltip and the hover area (the
// bar itself is too thin to hit).
export function CoverageBar({ counts }: { counts: CoverageBarCounts }) {
  const motionDisabled = usePrefersReducedMotion();
  const { segments, total } = coverageBarSegments(counts);
  if (total === 0) {
    return null;
  }
  return (
    <Flex h={pxToRem(6)} w="full" borderRadius="full" overflow="hidden" bg="bg.muted">
      {segments.map((segment) => (
        <Box
          key={segment.key}
          flexGrow={segment.value}
          flexShrink={1}
          flexBasis={0}
          bg={segment.bg}
          h="full"
          transition={motionDisabled ? undefined : BAR_SEGMENT_TRANSITION}
        />
      ))}
    </Flex>
  );
}
